<script setup lang="ts">
import { primeInputFocus } from '@/utils/prime-input-classes'
import AutoComplete, { type AutoCompletePassThroughMethodOptions } from 'primevue/autocomplete'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PredefinedPosition } from './types'

const props = withDefaults(
  defineProps<{
    label: string
    suggestions: PredefinedPosition[]
    placeholder?: string
    ariaLabel?: string
  }>(),
  {
    placeholder: undefined,
    ariaLabel: undefined,
  },
)

const emit = defineEmits<{
  update: [label: string]
}>()

const { t } = useI18n({ useScope: 'local' })

const CREATE_KEY = '__create__'

const text = ref(props.label)
const matches = ref<PredefinedPosition[]>([])
const typed = ref(false)

watch(
  () => props.label,
  (label) => (text.value = label),
)

const exactMatch = computed(() => exactSuggestion(text.value))

function exactSuggestion(value: string): PredefinedPosition | undefined {
  const needle = value.trim().toLowerCase()

  return props.suggestions.find((position) => position.label.toLowerCase() === needle)
}

function optionsFor(query: string): PredefinedPosition[] {
  const needle = query.trim().toLowerCase()

  if (needle === '' || exactSuggestion(query)) {
    return [...props.suggestions]
  }

  const filtered = typed.value
    ? props.suggestions.filter((position) => position.label.toLowerCase().includes(needle))
    : props.suggestions

  return [{ key: CREATE_KEY, label: query.trim() }, ...filtered]
}

function filterSuggestions(event: { query: string }): void {
  matches.value = optionsFor(event.query)
}

function update(value: string | PredefinedPosition): void {
  const label = typeof value === 'string' ? value : value.label

  typed.value = typeof value === 'string'
  text.value = label
  emit('update', label)
}

function optionClass({ context }: AutoCompletePassThroughMethodOptions): string {
  const option = context.option as PredefinedPosition

  if (option.key === CREATE_KEY) {
    return 'text-action! font-medium!'
  }

  return option.key === exactMatch.value?.key ? 'bg-info-subtle! text-action-hover! font-semibold!' : ''
}

const inputClass = [primeInputFocus, 'rounded-[var(--field-radius)]! pr-8!']

const dropdownClass =
  'absolute! inset-y-0! right-0! w-8! rounded-none! border-0! bg-transparent! text-ink-3! hover:bg-transparent! hover:text-ink! active:bg-transparent!'
</script>

<template>
  <AutoComplete
    :model-value="text"
    :suggestions="matches"
    option-label="label"
    dropdown
    fluid
    complete-on-focus
    scroll-height="min(60vh, 26rem)"
    :auto-option-focus="false"
    :placeholder="placeholder ?? t('placeholder')"
    :aria-label="ariaLabel"
    :show-empty-message="false"
    :pt="{
      pcInputText: { root: { class: inputClass } },
      dropdown: { class: dropdownClass },
      dropdownIcon: { class: 'h-3! w-3!' },
      option: optionClass,
    }"
    @blur="typed = false"
    @complete="filterSuggestions"
    @update:model-value="update">
    <template #option="{ option }">
      <span v-if="option.key === CREATE_KEY" class="flex w-full items-center gap-2">
        <span
          class="flex h-5 w-5 flex-none items-center justify-center rounded-full border-[1.5px] border-dashed border-current text-xs leading-none">
          +
        </span>
        <span class="min-w-0 truncate">{{ t('create', { label: option.label }) }}</span>
      </span>

      <span v-else class="flex w-full items-center gap-2">
        <span class="min-w-0 truncate">{{ option.label }}</span>
        <i v-if="option.key === exactMatch?.key" class="pi pi-check ml-auto text-xs" aria-hidden="true" />
      </span>
    </template>
  </AutoComplete>
</template>

<i18n lang="json">
{
  "de": {
    "placeholder": "Auswählen oder eigene Bezeichnung eintippen …",
    "create": "„{label}“ als eigene Bezeichnung"
  },
  "en": {
    "placeholder": "Pick one or type your own label …",
    "create": "“{label}” as your own label"
  }
}
</i18n>
